/**
 * Brücke zur Consent-API von Comments Anywhere (window.SCANY_Consent).
 *
 * Ziel: Die CE-eigene Consent-Snackbar ist nur noch nötig, wenn der Besucher
 * den Dienst in Pixelmate abgelehnt hat.
 *
 * - Pixelmate-Freigabe (Dienst oder Kategorie) → SCANY_Consent.grant().
 *   CE führt dabei auch eine zurückgehaltene Like-/Kommentar-Aktion aus.
 * - Widerruf in Pixelmate → SCANY_Consent.revoke(), aber nur, wenn Pixelmate
 *   die Einwilligung vorher selbst weitergegeben hat. Eine Einwilligung, die
 *   der Besucher nach einer Ablehnung direkt in der CE-Snackbar erteilt hat,
 *   bleibt so bestehen. Das CE-Cookie ist HttpOnly; ist CE auf der aktuellen
 *   Seite nicht geladen, bleibt der Widerruf vorgemerkt und wird auf der
 *   nächsten Seite mit CE nachgeholt.
 * - `scany:consent-required`: Hat der Besucher in Pixelmate noch nicht
 *   entschieden und ist das Banner sichtbar, übernimmt Pixelmate (Fokus aufs
 *   Banner) statt der CE-Snackbar. Nach einer Ablehnung bleibt das Event
 *   unbehandelt → CE zeigt seine eigene Snackbar.
 *
 * Serverseitiges Gegenstück: include/CommentsAnywhereIntegration.php
 * (Filter `scany_consent_mode` und `scany_has_consent`).
 */
const SERVICE_KEY = 'comments_anywhere';
const STORAGE_KEY = 'pixelmate_comments_anywhere_granted';

export class CommentsAnywhereConsent {
    constructor(banner) {
        this.banner = banner;

        const service = (banner?.config?.activeServices || {})[SERVICE_KEY];
        if (!service) return;

        this.category = typeof banner.normalizeServiceCategory === 'function'
            ? banner.normalizeServiceCategory(service.category)
            : (service.category || 'external');

        document.addEventListener('pixelmate:consent', (event) => this.onConsent(event.detail));
        document.addEventListener('scany:consent-required', (event) => this.onConsentRequired(event));
    }

    /**
     * Gleiche Regel wie ScriptBlocker/Pixelmate_Consent::has_service():
     * eine Einzelentscheidung sticht die Kategorie.
     *
     * @param {object|null} state window.pixelmateConsent bzw. Event-Detail
     * @return {boolean}
     */
    isGranted(state) {
        if (!state) return false;
        if (this.category === 'necessary') return true;

        const decision = state.services?.[SERVICE_KEY];
        if (typeof decision === 'boolean') return decision;

        return state.categories?.[this.category] === true;
    }

    onConsent(state) {
        const granted = this.isGranted(state);

        // CE-Scripts laufen im Footer; beim ersten Event (Seitenaufbau) kann
        // die API je nach Ladereihenfolge noch fehlen.
        if (this.api() || document.readyState === 'complete') {
            this.sync(granted);
        } else {
            window.addEventListener('load', () => this.sync(granted), { once: true });
        }
    }

    sync(granted) {
        const api = this.api();
        if (!api) return;

        if (granted) {
            api.grant();
            this.remember(true);
        } else if (this.recall()) {
            api.revoke();
            this.remember(false);
        }
    }

    onConsentRequired(event) {
        // Entscheidung liegt bereits vor (abgelehnt) → CE-Snackbar.
        if (window.pixelmateConsent) return;

        const bannerElement = this.banner?.bannerElement;
        if (!bannerElement || !bannerElement.isConnected) return;

        // CE hält die Aktion zurück und führt sie nach grant() aus.
        event.preventDefault();
        if (typeof this.banner.focusBanner === 'function') {
            this.banner.focusBanner();
        }
    }

    api() {
        const api = window.SCANY_Consent;
        return api && typeof api.grant === 'function' && typeof api.revoke === 'function' ? api : null;
    }

    remember(granted) {
        try {
            if (granted) window.localStorage.setItem(STORAGE_KEY, '1');
            else window.localStorage.removeItem(STORAGE_KEY);
        } catch (e) {
            // Kein Storage (Privatmodus o. Ä.): Widerruf wirkt dann nur auf Seiten mit CE.
        }
    }

    recall() {
        try {
            return window.localStorage.getItem(STORAGE_KEY) === '1';
        } catch (e) {
            return false;
        }
    }
}
